import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Headphones, Mic, ChevronRight, ChevronLeft, Volume2,
  BookOpen, PenTool, CheckCircle, PlayCircle, AlertCircle,
  ArrowRight, Monitor, Hand, Wifi, WifiOff, XCircle,
  Smartphone, Laptop, Globe, CircleAlert, RefreshCw
} from "lucide-react";

const STEPS = {
  DEVICE_CHECK: 0,
  WELCOME: 1,
  AUDIO_CHECK: 2,
  MIC_CHECK: 3,
  NAVIGATION: 4,
  READY: 5,
};

const GRADE_TIER = (grade) => {
  if (["K", "1", "2"].includes(grade)) return "k2";
  if (["3-4", "5-6"].includes(grade)) return "mid";
  return "upper";
};

// ── Device Check Logic ──────────────────────────────────────────
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let name = "Unknown";
  let version = "";
  let supported = false;

  if (ua.includes("Edg/")) {
    name = "Microsoft Edge";
    version = ua.match(/Edg\/([\d.]+)/)?.[1] || "";
    supported = true;
  } else if (ua.includes("Chrome/") && !ua.includes("Edg/")) {
    name = "Google Chrome";
    version = ua.match(/Chrome\/([\d.]+)/)?.[1] || "";
    supported = true;
  } else if (ua.includes("Firefox/")) {
    name = "Mozilla Firefox";
    version = ua.match(/Firefox\/([\d.]+)/)?.[1] || "";
    supported = true;
  } else if (ua.includes("Safari/") && !ua.includes("Chrome")) {
    name = "Safari";
    version = ua.match(/Version\/([\d.]+)/)?.[1] || "";
    supported = true;
  } else if (ua.includes("Trident") || ua.includes("MSIE")) {
    name = "Internet Explorer";
    version = ua.match(/(?:MSIE |rv:)([\d.]+)/)?.[1] || "";
    supported = false;
  }

  return { name, version, supported };
};

const getScreenInfo = () => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const isTooSmall = w < 768 || h < 500;
  const isSmall = w < 1024;
  return {
    width: w,
    height: h,
    status: isTooSmall ? "fail" : isSmall ? "warn" : "pass",
    label: `${w} x ${h}px`,
  };
};

const checkConnection = async () => {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn) {
    const speed = conn.downlink; // Mbps
    const effectiveType = conn.effectiveType; // 4g, 3g, 2g, slow-2g
    const isGood = speed >= 2 && (effectiveType === "4g" || effectiveType === "5g");
    const isFair = speed >= 0.5;
    return {
      speed: speed ? `${speed} Mbps` : "Unknown",
      type: effectiveType || "Unknown",
      status: isGood ? "pass" : isFair ? "warn" : "fail",
    };
  }
  // Fallback: ping test
  try {
    const start = performance.now();
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`, { method: "GET", cache: "no-store" });
    const latency = Math.round(performance.now() - start);
    return {
      speed: `${latency}ms latency`,
      type: latency < 300 ? "Fast" : latency < 1000 ? "Fair" : "Slow",
      status: latency < 300 ? "pass" : latency < 1000 ? "warn" : "fail",
    };
  } catch {
    return { speed: "Offline", type: "No connection", status: "fail" };
  }
};

const checkAudioSupport = () => {
  const hasWebAudio = !!(window.AudioContext || window.webkitAudioContext);
  const hasSpeechSynth = !!window.speechSynthesis;
  return {
    webAudio: hasWebAudio,
    speechSynth: hasSpeechSynth,
    status: hasWebAudio ? "pass" : "fail",
  };
};

const checkMicSupport = async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    return { available: false, permission: "unavailable", status: "fail" };
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasMic = devices.some((d) => d.kind === "audioinput");
    return {
      available: hasMic,
      permission: "not_requested",
      status: hasMic ? "pass" : "warn",
    };
  } catch {
    return { available: false, permission: "error", status: "warn" };
  }
};

// ── Status Badge ────────────────────────────────────────────────
const StatusBadge = ({ status, isK2 }) => {
  const config = {
    pass: { icon: CheckCircle, label: "Ready", bg: "bg-green-100", text: "text-green-700", border: "border-green-300" },
    warn: { icon: CircleAlert, label: "Warning", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-300" },
    fail: { icon: XCircle, label: "Issue", bg: "bg-red-50", text: "text-red-700", border: "border-red-300" },
    checking: { icon: RefreshCw, label: "Checking...", bg: "bg-gray-100", text: "text-gray-500", border: "border-gray-300" },
  };
  const c = config[status] || config.checking;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border ${c.bg} ${c.text} ${c.border} ${isK2 ? "text-sm" : "text-xs"} font-semibold`}>
      <Icon size={isK2 ? 16 : 12} className={status === "checking" ? "animate-spin" : ""} />
      {c.label}
    </span>
  );
};

// ── Device Profile Storage ──────────────────────────────────────
const DEVICE_PROFILE_KEY = "mosaic_device_profile";
const DEVICE_PROFILE_MAX_AGE_DAYS = 30;

const getSavedDeviceProfile = () => {
  try {
    const raw = localStorage.getItem(DEVICE_PROFILE_KEY);
    if (!raw) return null;
    const profile = JSON.parse(raw);
    const ageMs = Date.now() - profile.savedAt;
    const maxAgeMs = DEVICE_PROFILE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
    if (ageMs > maxAgeMs) {
      localStorage.removeItem(DEVICE_PROFILE_KEY);
      return null;
    }
    return profile;
  } catch {
    return null;
  }
};

const saveDeviceProfile = (results) => {
  try {
    localStorage.setItem(DEVICE_PROFILE_KEY, JSON.stringify({
      ...results,
      savedAt: Date.now(),
    }));
  } catch {}
};

const clearDeviceProfile = () => {
  try { localStorage.removeItem(DEVICE_PROFILE_KEY); } catch {}
};

// ══════════════════════════════════════════════════════════════════
const PreTestInstructions = ({ gradeLevel, studentName, onBeginExam, isDemo = false }) => {
  // Check for saved device profile to auto-skip
  const savedProfile = useRef(getSavedDeviceProfile());
  const [step, setStep] = useState(savedProfile.current ? STEPS.WELCOME : STEPS.DEVICE_CHECK);
  const [audioChecked, setAudioChecked] = useState(false);
  const [micChecked, setMicChecked] = useState(false);
  const [micStatus, setMicStatus] = useState("idle");
  const [isPlayingVoiceover, setIsPlayingVoiceover] = useState(false);
  const [voiceoverLoading, setVoiceoverLoading] = useState(false);
  const [deviceProfileSaved, setDeviceProfileSaved] = useState(!!savedProfile.current);
  const [showSavedBanner, setShowSavedBanner] = useState(!!savedProfile.current);
  const streamRef = useRef(null);
  const voiceoverAudioRef = useRef(null);
  const audioCache = useRef({});

  // Device check state
  const [deviceResults, setDeviceResults] = useState(savedProfile.current || null);
  const [deviceChecking, setDeviceChecking] = useState(!savedProfile.current);

  const tier = GRADE_TIER(gradeLevel);
  const isK2 = tier === "k2";

  // Step key mapping for backend TTS
  const STEP_KEYS = {
    [STEPS.DEVICE_CHECK]: `device_check_${isK2 ? "k2" : "upper"}`,
    [STEPS.WELCOME]: `welcome_${isK2 ? "k2" : "upper"}`,
    [STEPS.AUDIO_CHECK]: `audio_check_${isK2 ? "k2" : "upper"}`,
    [STEPS.MIC_CHECK]: `mic_check_${isK2 ? "k2" : "upper"}`,
    [STEPS.NAVIGATION]: `navigation_${isK2 ? "k2" : "upper"}`,
    [STEPS.READY]: `ready_${isK2 ? "k2" : "upper"}`,
  };

  // Dismiss saved banner after 4s
  useEffect(() => {
    if (showSavedBanner) {
      const t = setTimeout(() => setShowSavedBanner(false), 4000);
      return () => clearTimeout(t);
    }
  }, [showSavedBanner]);

  // Run device checks on mount (skip if saved profile)
  useEffect(() => {
    if (!savedProfile.current) {
      runDeviceChecks();
    }
  }, []);

  const runDeviceChecks = async () => {
    setDeviceChecking(true);
    const browser = getBrowserInfo();
    const screen = getScreenInfo();
    const audio = checkAudioSupport();
    const [connection, mic] = await Promise.all([checkConnection(), checkMicSupport()]);

    const results = { browser, screen, audio, connection, mic };

    // Overall status
    const statuses = [browser.supported ? "pass" : "fail", screen.status, audio.status, connection.status, mic.status];
    const hasFailure = statuses.includes("fail");
    const hasWarning = statuses.includes("warn");
    results.overall = hasFailure ? "fail" : hasWarning ? "warn" : "pass";

    setDeviceResults(results);
    setDeviceChecking(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (voiceoverAudioRef.current) {
        voiceoverAudioRef.current.pause();
        voiceoverAudioRef.current = null;
      }
    };
  }, []);

  // Fetch and play voiceover from backend OpenAI TTS (nova voice)
  const playVoiceover = async () => {
    // Stop any currently playing voiceover
    if (voiceoverAudioRef.current) {
      voiceoverAudioRef.current.pause();
      voiceoverAudioRef.current.currentTime = 0;
    }
    setIsPlayingVoiceover(false);

    const stepKey = STEP_KEYS[step];
    if (!stepKey) return;

    // Check local cache first
    if (audioCache.current[stepKey]) {
      const audio = new Audio(`data:audio/mp3;base64,${audioCache.current[stepKey]}`);
      voiceoverAudioRef.current = audio;
      audio.onplay = () => setIsPlayingVoiceover(true);
      audio.onended = () => setIsPlayingVoiceover(false);
      audio.onerror = () => setIsPlayingVoiceover(false);
      audio.play().catch(() => setIsPlayingVoiceover(false));
      return;
    }

    // Fetch from backend
    setVoiceoverLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audio/instruction/${stepKey}`);
      if (!res.ok) throw new Error("Failed to fetch voiceover");
      const data = await res.json();
      audioCache.current[stepKey] = data.audio;

      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      voiceoverAudioRef.current = audio;
      audio.onplay = () => setIsPlayingVoiceover(true);
      audio.onended = () => setIsPlayingVoiceover(false);
      audio.onerror = () => setIsPlayingVoiceover(false);
      audio.play().catch(() => setIsPlayingVoiceover(false));
    } catch (err) {
      console.warn("Voiceover fetch failed, falling back to browser TTS:", err);
      // Fallback to browser TTS if backend fails
      if (window.speechSynthesis) {
        const text = getFallbackText();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = isK2 ? 0.85 : 0.95;
        utterance.onstart = () => setIsPlayingVoiceover(true);
        utterance.onend = () => setIsPlayingVoiceover(false);
        window.speechSynthesis.speak(utterance);
      }
    } finally {
      setVoiceoverLoading(false);
    }
  };

  // Fallback text for browser TTS if backend is unavailable
  const getFallbackText = () => {
    const texts = {
      [STEPS.DEVICE_CHECK]: isK2 ? "Let's make sure your computer is ready." : "Checking device requirements.",
      [STEPS.WELCOME]: isK2 ? `Hi! Welcome to your practice test.` : `Welcome. This is your assessment.`,
      [STEPS.AUDIO_CHECK]: isK2 ? "Let's check your sound." : "Verifying audio.",
      [STEPS.MIC_CHECK]: isK2 ? "Let's test your microphone." : "Testing microphone.",
      [STEPS.NAVIGATION]: isK2 ? "Here's how the test works." : "Navigation overview.",
      [STEPS.READY]: isK2 ? "You're ready! Press start." : "Ready to begin.",
    };
    return texts[step] || "";
  };

  // Auto-play voiceover when step changes
  useEffect(() => {
    const timer = setTimeout(() => playVoiceover(), 600);
    // Pre-fetch next step audio in background
    const nextStepKey = STEP_KEYS[step + 1];
    if (nextStepKey && !audioCache.current[nextStepKey]) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audio/instruction/${nextStepKey}`)
        .then(r => r.json())
        .then(d => { if (d.audio) audioCache.current[nextStepKey] = d.audio; })
        .catch(() => {});
    }
    return () => clearTimeout(timer);
  }, [step]);

  // Audio check — play a short test tone
  const playTestSound = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.frequency.value = 440;
      oscillator.type = "sine";
      gainNode.gain.value = 0.3;
      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioCtx.close();
      }, 1500);
    } catch {
      // Fallback
    }
  };

  // Microphone check
  const testMicrophone = async () => {
    setMicStatus("listening");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setTimeout(() => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setMicStatus("success");
        setMicChecked(true);
      }, 2000);
    } catch {
      setMicStatus("error");
    }
  };

  const canProceed = () => {
    if (step === STEPS.DEVICE_CHECK && deviceChecking) return false;
    if (step === STEPS.AUDIO_CHECK && !audioChecked) return false;
    return true;
  };

  const nextStep = () => {
    if (voiceoverAudioRef.current) { voiceoverAudioRef.current.pause(); }
    setIsPlayingVoiceover(false);
    if (step < STEPS.READY) setStep(step + 1);
  };

  const prevStep = () => {
    if (voiceoverAudioRef.current) { voiceoverAudioRef.current.pause(); }
    setIsPlayingVoiceover(false);
    if (step > STEPS.DEVICE_CHECK) setStep(step - 1);
  };

  const stepLabels = ["Device", "Welcome", "Audio", "Microphone", "Navigation", "Ready"];

  // Helper: voiceover replay button
  const ReplayButton = () => (
    <button
      onClick={() => playVoiceover()}
      disabled={voiceoverLoading}
      className="mt-4 inline-flex items-center gap-2 text-sm text-[#002FA7] hover:underline disabled:opacity-50"
      data-testid="replay-voiceover"
    >
      <Volume2 size={16} className={isPlayingVoiceover ? "animate-pulse" : voiceoverLoading ? "animate-spin" : ""} />
      {voiceoverLoading ? "Loading voice..." : isPlayingVoiceover ? "Playing..." : "Hear instructions again"}
    </button>
  );

  return (
    <div className={`min-h-screen bg-gray-50 flex flex-col ${isK2 ? "k2-mode" : ""}`} data-testid="pre-test-instructions">
      {/* Saved device profile banner */}
      {showSavedBanner && (
        <div className="bg-green-50 border-b border-green-200 py-2 px-4 animate-fade-in-up" data-testid="saved-device-banner">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle size={16} />
              <span className={`font-medium ${isK2 ? "text-base" : "text-sm"}`}>
                {isK2 ? "Your computer was checked before — skipping!" : "Using saved device profile — device check skipped"}
              </span>
            </div>
            <button
              onClick={() => {
                clearDeviceProfile();
                setDeviceProfileSaved(false);
                setShowSavedBanner(false);
                savedProfile.current = null;
                setStep(STEPS.DEVICE_CHECK);
                setDeviceResults(null);
                setDeviceChecking(true);
                runDeviceChecks();
              }}
              className="text-xs text-green-600 hover:text-green-800 underline"
              data-testid="recheck-device-btn"
            >
              {isK2 ? "Check again" : "Re-check device"}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-[#002FA7] text-white py-4 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 px-3 py-1 text-xs font-bold tracking-wide">
              {isDemo ? "DEMO" : "ASSESSMENT"} SETUP
            </div>
            <span className="text-sm text-blue-200">Grade {gradeLevel}</span>
          </div>
          <div className="text-sm text-blue-200">{studentName}</div>
        </div>
      </div>

      {/* Step indicators */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {stepLabels.map((label, i) => (
              <div key={i} className="flex items-center gap-1">
                <div
                  className={`w-7 h-7 flex items-center justify-center text-xs font-bold border-2 transition-all ${
                    i < step
                      ? "bg-green-500 border-green-500 text-white"
                      : i === step
                      ? "bg-[#002FA7] border-[#002FA7] text-white"
                      : "bg-gray-100 border-gray-300 text-gray-400"
                  }`}
                  data-testid={`step-indicator-${i}`}
                >
                  {i < step ? <CheckCircle size={14} /> : i + 1}
                </div>
                <span
                  className={`text-xs font-semibold hidden sm:inline ${
                    i === step ? "text-[#002FA7]" : i < step ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  {label}
                </span>
                {i < stepLabels.length - 1 && (
                  <div className={`w-6 sm:w-12 h-0.5 mx-1 ${i < step ? "bg-green-400" : "bg-gray-200"}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="max-w-2xl w-full">

          {/* ── Step 0: Device Check ── */}
          {step === STEPS.DEVICE_CHECK && (
            <div className="bg-white border-2 border-gray-200 p-8 sm:p-10 animate-fade-in-up" data-testid="step-device-check">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Laptop size={36} className="text-slate-700" />
                </div>
                <h1 className={`font-bold mb-2 ${isK2 ? "text-3xl" : "text-2xl"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                  {isK2 ? "Checking Your Computer" : "Device Requirements Check"}
                </h1>
                <p className={`text-gray-500 ${isK2 ? "text-lg" : "text-sm"}`}>
                  {isK2 ? "Let's make sure everything works!" : "Verifying your device meets the assessment requirements."}
                </p>
              </div>

              {deviceChecking ? (
                <div className="text-center py-10">
                  <RefreshCw size={32} className="mx-auto text-[#002FA7] animate-spin mb-4" />
                  <p className={`text-gray-600 ${isK2 ? "text-xl" : ""}`}>
                    {isK2 ? "Checking..." : "Running device checks..."}
                  </p>
                </div>
              ) : deviceResults && (
                <div className="space-y-3" data-testid="device-results">
                  {/* Browser */}
                  <div className={`flex items-center justify-between p-4 border ${
                    deviceResults.browser.supported ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"
                  }`} data-testid="check-browser">
                    <div className="flex items-center gap-3">
                      <Globe size={isK2 ? 24 : 20} className="text-gray-600" />
                      <div>
                        <div className={`font-semibold ${isK2 ? "text-lg" : "text-sm"}`}>
                          {isK2 ? "Web Browser" : "Browser"}
                        </div>
                        <div className={`text-gray-500 ${isK2 ? "text-base" : "text-xs"}`}>
                          {deviceResults.browser.name} {deviceResults.browser.version ? `v${deviceResults.browser.version.split(".")[0]}` : ""}
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={deviceResults.browser.supported ? "pass" : "fail"} isK2={isK2} />
                  </div>

                  {/* Screen Size */}
                  <div className={`flex items-center justify-between p-4 border ${
                    deviceResults.screen.status === "pass" ? "border-green-200 bg-green-50/50" :
                    deviceResults.screen.status === "warn" ? "border-amber-200 bg-amber-50/50" :
                    "border-red-200 bg-red-50/50"
                  }`} data-testid="check-screen">
                    <div className="flex items-center gap-3">
                      <Monitor size={isK2 ? 24 : 20} className="text-gray-600" />
                      <div>
                        <div className={`font-semibold ${isK2 ? "text-lg" : "text-sm"}`}>
                          {isK2 ? "Screen Size" : "Display"}
                        </div>
                        <div className={`text-gray-500 ${isK2 ? "text-base" : "text-xs"}`}>
                          {deviceResults.screen.label}
                          {deviceResults.screen.status === "warn" && " — best on a larger screen"}
                          {deviceResults.screen.status === "fail" && " — screen too small"}
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={deviceResults.screen.status} isK2={isK2} />
                  </div>

                  {/* Connection */}
                  <div className={`flex items-center justify-between p-4 border ${
                    deviceResults.connection.status === "pass" ? "border-green-200 bg-green-50/50" :
                    deviceResults.connection.status === "warn" ? "border-amber-200 bg-amber-50/50" :
                    "border-red-200 bg-red-50/50"
                  }`} data-testid="check-connection">
                    <div className="flex items-center gap-3">
                      {deviceResults.connection.status === "fail" ? (
                        <WifiOff size={isK2 ? 24 : 20} className="text-red-500" />
                      ) : (
                        <Wifi size={isK2 ? 24 : 20} className="text-gray-600" />
                      )}
                      <div>
                        <div className={`font-semibold ${isK2 ? "text-lg" : "text-sm"}`}>
                          {isK2 ? "Internet" : "Connection"}
                        </div>
                        <div className={`text-gray-500 ${isK2 ? "text-base" : "text-xs"}`}>
                          {deviceResults.connection.speed} ({deviceResults.connection.type})
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={deviceResults.connection.status} isK2={isK2} />
                  </div>

                  {/* Audio */}
                  <div className={`flex items-center justify-between p-4 border ${
                    deviceResults.audio.status === "pass" ? "border-green-200 bg-green-50/50" : "border-red-200 bg-red-50/50"
                  }`} data-testid="check-audio">
                    <div className="flex items-center gap-3">
                      <Headphones size={isK2 ? 24 : 20} className="text-gray-600" />
                      <div>
                        <div className={`font-semibold ${isK2 ? "text-lg" : "text-sm"}`}>
                          {isK2 ? "Sound" : "Audio Support"}
                        </div>
                        <div className={`text-gray-500 ${isK2 ? "text-base" : "text-xs"}`}>
                          {deviceResults.audio.status === "pass" ? "Audio playback supported" : "Audio not supported"}
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={deviceResults.audio.status} isK2={isK2} />
                  </div>

                  {/* Microphone */}
                  <div className={`flex items-center justify-between p-4 border ${
                    deviceResults.mic.status === "pass" ? "border-green-200 bg-green-50/50" :
                    deviceResults.mic.status === "warn" ? "border-amber-200 bg-amber-50/50" :
                    "border-red-200 bg-red-50/50"
                  }`} data-testid="check-mic">
                    <div className="flex items-center gap-3">
                      <Mic size={isK2 ? 24 : 20} className="text-gray-600" />
                      <div>
                        <div className={`font-semibold ${isK2 ? "text-lg" : "text-sm"}`}>
                          {isK2 ? "Microphone" : "Microphone"}
                        </div>
                        <div className={`text-gray-500 ${isK2 ? "text-base" : "text-xs"}`}>
                          {deviceResults.mic.status === "pass"
                            ? "Microphone detected"
                            : deviceResults.mic.status === "warn"
                            ? "Could not verify — will test in next steps"
                            : "No microphone found"}
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={deviceResults.mic.status} isK2={isK2} />
                  </div>

                  {/* Overall verdict */}
                  <div className={`mt-5 p-4 text-center border-2 ${
                    deviceResults.overall === "pass"
                      ? "border-green-400 bg-green-50"
                      : deviceResults.overall === "warn"
                      ? "border-amber-400 bg-amber-50"
                      : "border-red-400 bg-red-50"
                  }`} data-testid="device-verdict">
                    {deviceResults.overall === "pass" && (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle size={isK2 ? 24 : 20} className="text-green-600" />
                        <span className={`font-bold text-green-700 ${isK2 ? "text-xl" : "text-sm"}`}>
                          {isK2 ? "Your computer is ready!" : "All checks passed — device is ready"}
                        </span>
                      </div>
                    )}
                    {deviceResults.overall === "warn" && (
                      <div>
                        <div className="flex items-center justify-center gap-2">
                          <CircleAlert size={isK2 ? 24 : 20} className="text-amber-600" />
                          <span className={`font-bold text-amber-700 ${isK2 ? "text-xl" : "text-sm"}`}>
                            {isK2 ? "Almost ready!" : "Minor issues detected — you can still proceed"}
                          </span>
                        </div>
                        <p className={`text-amber-600 mt-1 ${isK2 ? "text-base" : "text-xs"}`}>
                          {isK2
                            ? "Some things could be better, but you can still take the test."
                            : "Some items showed warnings. The assessment will still work, but performance may be affected."}
                        </p>
                      </div>
                    )}
                    {deviceResults.overall === "fail" && (
                      <div>
                        <div className="flex items-center justify-center gap-2">
                          <XCircle size={isK2 ? 24 : 20} className="text-red-600" />
                          <span className={`font-bold text-red-700 ${isK2 ? "text-xl" : "text-sm"}`}>
                            {isK2 ? "Something needs fixing" : "Issues found — review the items above"}
                          </span>
                        </div>
                        <p className={`text-red-600 mt-1 ${isK2 ? "text-base" : "text-xs"}`}>
                          {isK2
                            ? "Ask your teacher for help before starting."
                            : "One or more critical requirements are not met. You may still proceed, but some features may not work correctly."}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Re-run button */}
                  <div className="text-center mt-2 space-y-2">
                    <button
                      onClick={runDeviceChecks}
                      className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#002FA7] hover:underline"
                      data-testid="rerun-device-check"
                    >
                      <RefreshCw size={14} />
                      {isK2 ? "Check again" : "Re-run checks"}
                    </button>

                    {/* Save device profile toggle */}
                    {deviceResults?.overall !== "fail" && (
                      <div className="pt-2">
                        {!deviceProfileSaved ? (
                          <button
                            onClick={() => {
                              saveDeviceProfile(deviceResults);
                              setDeviceProfileSaved(true);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#002FA7] border border-[#002FA7]/30 bg-blue-50/50 hover:bg-blue-100/50 transition-colors"
                            data-testid="save-device-profile-btn"
                          >
                            <Laptop size={16} />
                            {isK2 ? "Remember this computer" : "Remember this device (skip next time)"}
                          </button>
                        ) : (
                          <div className="inline-flex items-center gap-2 text-sm text-green-600 font-medium" data-testid="device-profile-saved">
                            <CheckCircle size={16} />
                            {isK2 ? "Computer remembered!" : "Device profile saved — check will be skipped next time"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="text-center">
                <ReplayButton />
              </div>
            </div>
          )}

          {/* ── Step 1: Welcome ── */}
          {step === STEPS.WELCOME && (
            <div className="bg-white border-2 border-gray-200 p-8 sm:p-10 text-center animate-fade-in-up" data-testid="step-welcome">
              <div className="w-20 h-20 bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <Monitor size={36} className="text-[#002FA7]" />
              </div>
              <h1 className={`font-bold mb-4 ${isK2 ? "text-3xl" : "text-2xl"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                {isK2 ? `Hi, ${studentName}!` : `Welcome, ${studentName}`}
              </h1>
              <p className={`text-gray-600 mb-6 leading-relaxed ${isK2 ? "text-xl" : "text-base"}`}>
                {isK2
                  ? "You're about to take a practice test! You will listen, speak, read, and write. Let's make sure everything is ready."
                  : `You are about to begin a ${isDemo ? "demo" : "practice"} assessment for Grade ${gradeLevel}. This test has four sections:`}
              </p>

              <div className={`grid grid-cols-2 gap-3 mb-6 ${isK2 ? "gap-4" : ""}`}>
                {[
                  { icon: Headphones, label: "Listening", color: "bg-purple-100 text-purple-700" },
                  { icon: Mic, label: "Speaking", color: "bg-blue-100 text-blue-700" },
                  { icon: BookOpen, label: "Reading", color: "bg-green-100 text-green-700" },
                  { icon: PenTool, label: "Writing", color: "bg-orange-100 text-orange-700" },
                ].map(({ icon: Icon, label, color }) => (
                  <div key={label} className={`${color} p-3 flex items-center gap-3 ${isK2 ? "p-4" : ""}`}>
                    <Icon size={isK2 ? 28 : 20} />
                    <span className={`font-semibold ${isK2 ? "text-xl" : "text-sm"}`}>{label}</span>
                  </div>
                ))}
              </div>

              <p className={`text-gray-500 ${isK2 ? "text-lg" : "text-sm"}`}>
                {isK2
                  ? "Next, we'll check your sound and microphone."
                  : "Next, we'll verify your audio and microphone are working correctly."}
              </p>
              <ReplayButton />
            </div>
          )}

          {/* ── Step 2: Audio Check ── */}
          {step === STEPS.AUDIO_CHECK && (
            <div className="bg-white border-2 border-gray-200 p-8 sm:p-10 text-center animate-fade-in-up" data-testid="step-audio-check">
              <div className="w-20 h-20 bg-purple-100 flex items-center justify-center mx-auto mb-6">
                <Headphones size={36} className="text-purple-700" />
              </div>
              <h2 className={`font-bold mb-4 ${isK2 ? "text-3xl" : "text-2xl"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                {isK2 ? "Can You Hear?" : "Audio Check"}
              </h2>
              <p className={`text-gray-600 mb-6 ${isK2 ? "text-xl" : "text-base"}`}>
                {isK2
                  ? "Put on your headphones. Press the purple button to hear a sound."
                  : "Make sure your headphones or speakers are connected. Click the button below to play a test tone."}
              </p>

              <div className="space-y-4">
                <Button
                  onClick={playTestSound}
                  className={`bg-purple-600 hover:bg-purple-700 text-white ${isK2 ? "h-16 text-xl px-10" : "h-12 px-8"}`}
                  data-testid="play-test-sound"
                >
                  <PlayCircle size={isK2 ? 28 : 20} className="mr-2" />
                  {isK2 ? "Play Sound" : "Play Test Sound"}
                </Button>

                {!audioChecked ? (
                  <div>
                    <Button
                      onClick={() => setAudioChecked(true)}
                      variant="outline"
                      className={`border-2 border-green-500 text-green-700 hover:bg-green-50 ${isK2 ? "h-14 text-lg px-8" : "h-11 px-6"}`}
                      data-testid="audio-confirm"
                    >
                      <CheckCircle size={isK2 ? 24 : 18} className="mr-2" />
                      {isK2 ? "I can hear it!" : "I can hear the sound"}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-green-600 font-semibold" data-testid="audio-confirmed">
                    <CheckCircle size={20} />
                    <span className={isK2 ? "text-xl" : ""}>Audio is working!</span>
                  </div>
                )}
              </div>

              {!audioChecked && (
                <p className={`text-amber-600 mt-4 ${isK2 ? "text-base" : "text-sm"}`}>
                  <AlertCircle size={14} className="inline mr-1" />
                  {isK2 ? "Can't hear? Ask your teacher for help." : "If you cannot hear the sound, check your headphones or volume settings."}
                </p>
              )}
              <ReplayButton />
            </div>
          )}

          {/* ── Step 3: Microphone Check ── */}
          {step === STEPS.MIC_CHECK && (
            <div className="bg-white border-2 border-gray-200 p-8 sm:p-10 text-center animate-fade-in-up" data-testid="step-mic-check">
              <div className="w-20 h-20 bg-red-100 flex items-center justify-center mx-auto mb-6">
                <Mic size={36} className="text-red-600" />
              </div>
              <h2 className={`font-bold mb-4 ${isK2 ? "text-3xl" : "text-2xl"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                {isK2 ? "Let's Test Your Voice!" : "Microphone Check"}
              </h2>
              <p className={`text-gray-600 mb-6 ${isK2 ? "text-xl" : "text-base"}`}>
                {isK2
                  ? "Press the button and say your name. We want to hear you!"
                  : "Click the button below and speak for a moment. This verifies your microphone works for the Speaking section."}
              </p>

              <div className="space-y-4">
                {micStatus === "idle" && (
                  <Button
                    onClick={testMicrophone}
                    className={`bg-red-600 hover:bg-red-700 text-white ${isK2 ? "h-16 text-xl px-10" : "h-12 px-8"}`}
                    data-testid="test-mic-btn"
                  >
                    <Mic size={isK2 ? 28 : 20} className="mr-2" />
                    {isK2 ? "Test My Voice" : "Test Microphone"}
                  </Button>
                )}

                {micStatus === "listening" && (
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                      <Mic size={28} className="text-white" />
                    </div>
                    <span className={`text-red-600 font-semibold ${isK2 ? "text-xl" : ""}`}>
                      {isK2 ? "Say something!" : "Listening... speak now"}
                    </span>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className="w-2 bg-red-400 rounded-full animate-pulse"
                          style={{ height: `${12 + Math.random() * 24}px`, animationDelay: `${i * 0.1}s` }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {micStatus === "success" && (
                  <div className="flex items-center justify-center gap-2 text-green-600 font-semibold" data-testid="mic-confirmed">
                    <CheckCircle size={20} />
                    <span className={isK2 ? "text-xl" : ""}>Microphone is working!</span>
                  </div>
                )}

                {micStatus === "error" && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2 text-amber-600 font-semibold">
                      <AlertCircle size={20} />
                      <span>{isK2 ? "We can't hear you." : "Microphone access denied"}</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {isK2
                        ? "Ask your teacher for help with the microphone."
                        : "Please allow microphone access in your browser settings, or check that a microphone is connected."}
                    </p>
                    <Button onClick={() => setMicStatus("idle")} variant="outline" className="h-10" data-testid="retry-mic">
                      Try Again
                    </Button>
                  </div>
                )}

                {!micChecked && micStatus !== "listening" && micStatus !== "success" && (
                  <button
                    onClick={() => { setMicChecked(true); nextStep(); }}
                    className="text-sm text-gray-400 hover:text-gray-600 underline"
                    data-testid="skip-mic-check"
                  >
                    {isK2 ? "Skip this step" : "Skip microphone check"}
                  </button>
                )}
              </div>
              <ReplayButton />
            </div>
          )}

          {/* ── Step 4: Navigation Tutorial ── */}
          {step === STEPS.NAVIGATION && (
            <div className="bg-white border-2 border-gray-200 p-8 sm:p-10 animate-fade-in-up" data-testid="step-navigation">
              <div className="w-20 h-20 bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <Hand size={36} className="text-[#002FA7]" />
              </div>
              <h2 className={`font-bold mb-6 text-center ${isK2 ? "text-3xl" : "text-2xl"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                {isK2 ? "How the Test Works" : "Test Navigation"}
              </h2>

              <div className={`space-y-4 ${isK2 ? "text-lg" : "text-sm"}`}>
                {[
                  {
                    icon: <ChevronRight size={isK2 ? 28 : 20} className="text-[#002FA7]" />,
                    title: isK2 ? "Next Button" : "Next / Previous",
                    desc: isK2
                      ? 'Press "Next" to go to the next question. Press "Back" to go back.'
                      : "Use the Next and Previous buttons at the bottom of each question to navigate forward and backward.",
                  },
                  {
                    icon: <Headphones size={isK2 ? 28 : 20} className="text-purple-600" />,
                    title: isK2 ? "Four Parts" : "Modality Tabs",
                    desc: isK2
                      ? "You will do Listening, Speaking, Reading, and Writing. The tabs at the top show which part you're on."
                      : "The tabs at the top (Listening, Speaking, Reading, Writing) show your current section and progress in each.",
                  },
                  {
                    icon: <PlayCircle size={isK2 ? 28 : 20} className="text-green-600" />,
                    title: isK2 ? "Listen Carefully" : "Audio Playback",
                    desc: isK2
                      ? 'In Listening, press "Play Audio" to hear the question. You can play it 2 times.'
                      : "During Listening questions, press Play Audio to hear the narration. You may replay up to 2 times per question.",
                  },
                  {
                    icon: <Mic size={isK2 ? 28 : 20} className="text-red-600" />,
                    title: isK2 ? "Speak Out Loud" : "Voice Recording",
                    desc: isK2
                      ? "In Speaking, press the red button and talk. Your words will appear on the screen."
                      : "For Speaking tasks, click the record button and speak clearly. Your response is automatically transcribed.",
                  },
                ].map(({ icon, title, desc }, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 border border-gray-200">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-white border border-gray-200">
                      {icon}
                    </div>
                    <div>
                      <div className={`font-bold ${isK2 ? "text-xl" : "text-sm"}`}>{title}</div>
                      <p className={`text-gray-600 mt-1 ${isK2 ? "text-lg leading-relaxed" : "text-sm"}`}>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <ReplayButton />
              </div>
            </div>
          )}

          {/* ── Step 5: Ready ── */}
          {step === STEPS.READY && (
            <div className="bg-white border-2 border-gray-200 p-8 sm:p-10 text-center animate-fade-in-up" data-testid="step-ready">
              <div className="w-24 h-24 bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={48} className="text-green-600" />
              </div>
              <h2 className={`font-bold mb-4 ${isK2 ? "text-3xl" : "text-2xl"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                {isK2 ? "You're Ready!" : "Ready to Begin"}
              </h2>
              <p className={`text-gray-600 mb-2 ${isK2 ? "text-xl" : "text-base"}`}>
                {isK2
                  ? "Everything is set up. Press the green button when you want to start!"
                  : "Your device is set up and ready. Click the button below when you're ready to begin your assessment."}
              </p>

              {/* Checklist summary */}
              <div className="inline-block text-left my-6 space-y-2">
                <div className="flex items-center gap-3">
                  <CheckCircle size={18} className={deviceResults?.overall !== "fail" ? "text-green-500" : "text-amber-400"} />
                  <span className={`${isK2 ? "text-lg" : "text-sm"} text-gray-700`}>
                    {isK2 ? "Computer checked" : "Device requirements verified"}
                    {deviceProfileSaved && <span className="text-xs text-green-500 ml-1">(saved)</span>}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={18} className={audioChecked ? "text-green-500" : "text-gray-300"} />
                  <span className={`${isK2 ? "text-lg" : "text-sm"} ${audioChecked ? "text-gray-700" : "text-gray-400"}`}>
                    {isK2 ? "Sound works" : "Audio verified"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={18} className={micChecked ? "text-green-500" : "text-gray-300"} />
                  <span className={`${isK2 ? "text-lg" : "text-sm"} ${micChecked ? "text-gray-700" : "text-gray-400"}`}>
                    {isK2 ? "Microphone works" : "Microphone verified"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={18} className="text-green-500" />
                  <span className={`text-gray-700 ${isK2 ? "text-lg" : "text-sm"}`}>
                    {isK2 ? "You know how to use the buttons" : "Navigation reviewed"}
                  </span>
                </div>
              </div>

              <div>
                <Button
                  onClick={onBeginExam}
                  className={`bg-green-600 hover:bg-green-700 text-white font-bold ${isK2 ? "h-20 text-2xl px-14" : "h-14 text-lg px-10"}`}
                  data-testid="begin-assessment-btn"
                >
                  <ArrowRight size={isK2 ? 28 : 22} className="mr-3" />
                  {isK2 ? "Start My Test!" : "Begin Assessment"}
                </Button>
              </div>
              <ReplayButton />
            </div>
          )}
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="bg-white border-t border-gray-200 py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={step === STEPS.DEVICE_CHECK}
            className={`btn-sharp ${isK2 ? "h-14 px-6 text-lg" : "h-11 px-5"}`}
            data-testid="instructions-prev"
          >
            <ChevronLeft size={isK2 ? 22 : 18} className="mr-1" />
            {isK2 ? "Back" : "Previous"}
          </Button>

          <div className="flex gap-1.5">
            {stepLabels.map((_, i) => (
              <div
                key={i}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  i === step ? "bg-[#002FA7] scale-125" : i < step ? "bg-green-400" : "bg-gray-300"
                }`}
              />
            ))}
          </div>

          {step < STEPS.READY ? (
            <Button
              onClick={nextStep}
              disabled={!canProceed()}
              className={`btn-sharp-primary ${isK2 ? "h-14 px-6 text-lg" : "h-11 px-5"}`}
              data-testid="instructions-next"
            >
              {isK2 ? "Next" : "Continue"}
              <ChevronRight size={isK2 ? 22 : 18} className="ml-1" />
            </Button>
          ) : (
            <Button
              onClick={onBeginExam}
              className={`bg-green-600 hover:bg-green-700 text-white font-bold ${isK2 ? "h-14 px-6 text-lg" : "h-11 px-5"}`}
              data-testid="begin-assessment-footer-btn"
            >
              {isK2 ? "Start!" : "Begin"}
              <ArrowRight size={isK2 ? 22 : 18} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreTestInstructions;
